import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Select, Input, Typography, Tag } from 'antd';
import { catalogSearch, getItemOnHand } from '../api';
import type { CatalogSearchResult } from '../api';
import ItemThumb from './ItemThumb';
import { useItemPictures } from '../hooks/useItemPictures';

const { Link } = Typography;

export interface PartNumberComboboxProps {
  value?: number | null;
  onChange?: (catalogItemId: number | null, item: CatalogSearchResult | null) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  allowFreeText?: boolean;
  freeTextValue?: string;
  onFreeTextChange?: (text: string) => void;
  showOnHand?: boolean;
}

const fmtOption = (item: CatalogSearchResult): string => {
  const price =
    item.defaultUnitPrice !== null && item.defaultUnitPrice !== undefined
      ? item.defaultUnitPrice.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : null;
  const parts = [item.partNumber, item.description];
  if (item.descriptionAr) parts.push(item.descriptionAr);
  if (price !== null) {
    parts.push(`${price}${item.currency?.code ? ` ${item.currency.code}` : ''}`);
  }
  return parts.join(' — ');
};

const PartNumberCombobox: React.FC<PartNumberComboboxProps> = ({
  value,
  onChange,
  placeholder = 'Search part number...',
  disabled = false,
  style,
  allowFreeText = false,
  freeTextValue = '',
  onFreeTextChange,
  showOnHand = false,
}) => {
  const [options, setOptions] = useState<CatalogSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [freeMode, setFreeMode] = useState(false);
  const [onHandMap, setOnHandMap] = useState<Record<number, number>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last non-empty result set. antd clears the search text on select/blur, which
  // calls onSearch('') before onSelect runs; looking the item up here instead of
  // in `options` keeps the pick from being lost.
  const resultsRef = useRef<CatalogSearchResult[]>([]);
  const onHandUnavailableRef = useRef(false);
  // Item pictures (2026-09-22): a thumbnail beside every suggestion, so two similar crêpes are
  // told apart before the line is added. The click belongs to the option, not to a lightbox.
  const optionIds = useMemo(() => options.map((o) => o.id), [options]);
  const { pictures } = useItemPictures(optionIds);

  const handleSearch = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length === 0) {
      // Keep the current options so a just-selected value still has its label.
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await catalogSearch(query.trim(), 20);
        resultsRef.current = results;
        setOptions(results);
        setAccessDenied(false);
        // Fetch on-hand for each result if showOnHand is enabled
        if (showOnHand && results.length > 0 && !onHandUnavailableRef.current) {
          const onHandPromises = results.map((r) =>
            getItemOnHand(r.id)
              .then((data) => ({ id: r.id, qty: data.totalOnHand }))
              .catch((e: any) => {
                // Inventory module not deployed (404): stop asking for this session.
                if (e?.response?.status === 404) onHandUnavailableRef.current = true;
                return { id: r.id, qty: 0 };
              })
          );
          const resolved = await Promise.all(onHandPromises);
          setOnHandMap((prev) => {
            const next = { ...prev };
            for (const { id, qty } of resolved) next[id] = qty;
            return next;
          });
        }
      } catch (err: any) {
        setOptions([]);
        setAccessDenied(err?.response?.status === 403);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleSelect = (selectedId: number) => {
    const found =
      options.find((o) => o.id === selectedId) ??
      resultsRef.current.find((o) => o.id === selectedId) ??
      null;
    onChange?.(selectedId, found);
  };

  const handleClear = () => {
    setOptions([]);
    onChange?.(null, null);
  };

  const switchToFreeText = () => {
    setFreeMode(true);
    onChange?.(null, null);
  };

  const switchToCatalog = () => {
    setFreeMode(false);
    onFreeTextChange?.('');
  };

  if (freeMode) {
    return (
      <div style={style}>
        <Input
          value={freeTextValue}
          onChange={(e) => onFreeTextChange?.(e.target.value)}
          placeholder="Enter part number manually"
          disabled={disabled}
          suffix={
            <Link style={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={switchToCatalog}>
              Search catalog
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div style={style}>
      <Select<number>
        showSearch
        allowClear
        filterOption={false}
        optionLabelProp="label"
        value={value ?? undefined}
        placeholder={placeholder}
        disabled={disabled}
        loading={searching}
        style={{ width: '100%' }}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        // A 36px thumbnail makes the row taller than antd's virtual-list default (24px).
        listItemHeight={46}
        listHeight={322}
        notFoundContent={
          searching
            ? 'Searching...'
            : accessDenied
              ? 'No catalog access — ask an admin to grant Parts Catalog (read)'
              : 'No items found'
        }
      >
        {options.map((item) => (
          <Select.Option key={item.id} value={item.id} label={item.partNumber}>
            <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <ItemThumb url={pictures[item.id]?.url} clickable={false} />
              <span style={{ minWidth: 0 }}>
                {fmtOption(item)}
                {showOnHand && onHandMap[item.id] !== undefined && (
                  <Tag
                    color={onHandMap[item.id] > 0 ? 'green' : 'red'}
                    style={{ marginLeft: 8, fontSize: 11 }}
                  >
                    {onHandMap[item.id]} on hand
                  </Tag>
                )}
              </span>
            </span>
          </Select.Option>
        ))}
      </Select>
      {allowFreeText && (
        <div style={{ marginTop: 4 }}>
          <Link style={{ fontSize: 12 }} onClick={switchToFreeText}>
            Not in catalog? Type manually
          </Link>
        </div>
      )}
    </div>
  );
};

export default PartNumberCombobox;
